// Dibangkitkan dari api/openapi.yaml (120 skema komponen
// + 39 skema jawaban sebaris). JANGAN disunting.
// Jalankan `pnpm --filter @assist/contracts generate`.
import { z } from 'zod';
/** ULID Crockford Base32 26 karakter, DIBUAT KLIEN (ADR-5). */
export const UlidSchema = z.string().regex(new RegExp("^[0-7][0-9ABCDEFGHJKMNPQRSTVWXYZ]{25}$"));
export const DeviceInfoSchema = z.object({
    "device_id": z.string().optional(),
    "device_label": z.string().optional(),
    "platform": z.enum(["WEB", "ANDROID", "IOS", "CLI"]).optional(),
}).passthrough();
export const AcceptInvitationRequestSchema = z.object({
    "token": z.string(),
    "password": z.string().min(12),
    "notice_acknowledged": z.boolean(),
    "notice_id": UlidSchema.nullable().optional(),
    "locale": z.enum(["id-ID", "en-US"]).optional(),
    "device": DeviceInfoSchema.optional(),
}).passthrough();
export const AcknowledgedSchema = z.object({
    "ok": z.boolean().optional(),
    "audit_log_id": UlidSchema.nullable().optional(),
}).passthrough();
export const AgreementAcceptRequestSchema = z.object({
    "accepted_by_name": z.string().min(2).max(160),
    "accepted_by_title": z.string().min(2).max(160),
    "locale": z.enum(["id-ID", "en-US"]).optional(),
}).passthrough();
export const AgreementAcceptResultSchema = z.object({
    "acceptance_id": UlidSchema,
    "agreement_version": z.number().int(),
    "tenant_state": z.string(),
}).passthrough();
export const AgreementAcceptanceSchema = z.object({
    "id": UlidSchema,
    "agreement_version": z.number().int(),
    "agreement_locale": z.string().optional(),
    "accepted_by_name": z.string(),
    "accepted_by_title": z.string(),
    "accepted_at": z.string(),
}).passthrough();
export const InstrumentCriterionSchema = z.object({
    "id": UlidSchema.optional(),
    "seq": z.number().int().optional(),
    "code": z.string().optional(),
    "text": z.string().optional(),
    "guidance": z.string().nullable().optional(),
    "max_score": z.number().optional(),
    "weight": z.number().optional(),
    "requires_photo_on_fail": z.boolean().optional(),
}).passthrough();
export const InstrumentSchema = z.object({
    "id": UlidSchema.optional(),
    "code": z.string().optional(),
    "title": z.string().optional(),
    "version": z.number().int().optional(),
    "state": z.enum(["DRAFT", "PUBLISHED", "WITHDRAWN"]).optional(),
    "max_score": z.number().optional(),
    "scoring": z.record(z.string(), z.unknown()).optional(),
    "criteria": z.array(InstrumentCriterionSchema).optional(),
}).passthrough();
export const LocationRefSchema = z.object({
    "id": UlidSchema.optional(),
    "code": z.string().optional(),
    "name": z.string().optional(),
    "org_unit_id": UlidSchema.optional(),
}).passthrough();
export const UserRefSchema = z.object({
    "id": UlidSchema.optional(),
    "full_name": z.string().nullable().optional(),
    "avatar_url": z.string().nullable().optional(),
}).passthrough();
export const AssessmentAnswerSchema = z.object({
    "criterion_id": UlidSchema,
    "conforms": z.boolean().nullable().optional(),
    "score": z.number().nullable().optional(),
    "note": z.string().nullable().optional(),
    "attachments": z.array(z.object({
        "file_id": UlidSchema.optional(),
    }).passthrough()).optional(),
    "spawned_item_code": z.string().nullable().optional(),
}).passthrough();
export const AvailableTransitionSchema = z.object({
    "code": z.string().optional(),
    "label_id": z.string().optional(),
    "label_en": z.string().optional(),
    "to": z.string().nullable().optional(),
    "reason_required": z.boolean().optional(),
    "blocked_by": z.string().nullable().optional(),
    "requires_input": z.array(z.string()).optional(),
    "requires": z.array(z.string()).optional(),
    "destructive": z.boolean().optional(),
}).passthrough();
export const AssessmentSchema = z.object({
    "id": UlidSchema.optional(),
    "instrument": InstrumentSchema.optional(),
    "instrument_version": z.number().int().optional(),
    "location": LocationRefSchema.optional(),
    "auditor": UserRefSchema.optional(),
    "state": z.enum(["SCHEDULED", "IN_PROGRESS", "SUBMITTED", "VOIDED"]).optional(),
    "scheduled_at": z.string().nullable().optional(),
    "started_at": z.string().nullable().optional(),
    "submitted_at": z.string().nullable().optional(),
    "total_score": z.number().nullable().optional(),
    "max_score": z.number().nullable().optional(),
    "score_pct": z.number().nullable().optional(),
    "band": z.string().nullable().optional(),
    "spawned_item_count": z.number().int().optional(),
    "captured_offline": z.boolean().optional(),
    "answers": z.array(AssessmentAnswerSchema).optional(),
    "available_transitions": z.array(AvailableTransitionSchema).optional(),
}).passthrough();
/** Penyimpanan jawaban bersifat idempoten per mutation_id dan menggantikan jawaban untuk criterion yang disebut. Jawaban yang tidak disebut tidak disentuh, sehingga audit luring dapat mengirim sebagian. */
export const AssessmentAnswerPutSchema = z.object({
    "mutation_id": UlidSchema,
    "instrument_version": z.number().int().optional(),
    "answers": z.array(AssessmentAnswerSchema),
}).passthrough();
export const AssessmentCreateSchema = z.object({
    "id": UlidSchema,
    "instrument_id": UlidSchema,
    "instrument_version": z.number().int().optional(),
    "location_id": UlidSchema,
    "auditor_id": UlidSchema.nullable().optional(),
    "scheduled_at": z.string().nullable().optional(),
}).passthrough();
export const AssetRefSchema = z.object({
    "id": UlidSchema.optional(),
    "code": z.string().optional(),
    "name": z.string().optional(),
}).passthrough();
export const AssetSchema = AssetRefSchema.and(z.object({
    "location_id": UlidSchema.optional(),
    "asset_class": z.string().nullable().optional(),
    "state": z.enum(["ACTIVE", "INACTIVE"]).optional(),
}).passthrough());
export const AttachmentSchema = z.object({
    "id": UlidSchema.optional(),
    "file_id": UlidSchema.optional(),
    "role": z.enum(["BEFORE", "AFTER", "EVIDENCE"]).optional(),
    "download_url": z.string().optional(),
    "width_px": z.number().int().nullable().optional(),
    "height_px": z.number().int().nullable().optional(),
    "byte_size": z.number().int().nullable().optional(),
    "caption": z.string().nullable().optional(),
}).passthrough();
/** Rantai hash yang hanya konsisten dengan dirinya sendiri dapat disusun ulang oleh pemegang basis data. Jangkar harian ke penyimpanan yang tidak dapat ditulis basis data membuatnya dapat dibuktikan terhadap titik di luar DB. */
export const AuditAnchorSchema = z.object({
    "chain_key": z.string().optional(),
    "chain_seq": z.number().int().optional(),
    "row_hash": z.string().optional(),
    "anchored_at": z.string().optional(),
    "anchor_ref": z.string().optional(),
}).passthrough();
/** EKSPOR SELESAI DI DALAM PERMINTAANNYA. Bentuk asinkron 202+id DIHAPUS karena manifest tidak punya satu pun rute untuk menjajakinya: sebuah id yang tidak dapat ditanyakan kembali adalah tanda terima untuk pekerjaan yang pemiliknya tidak pernah dapat tahu selesai atau tidak. Konsekuensinya dinyatakan: ekspor dibatasi jumlah baris, dan penolakannya (AUDIT_EXPORT_TOO_LARGE) membawa angka yang ditemukan beserta batasnya. */
export const AuditExportSchema = z.object({
    "id": UlidSchema,
    "format": z.enum(["CSV"]),
    "state": z.enum(["READY"]),
    "row_count": z.number().int(),
    "filename": z.string(),
    "media_type": z.string(),
    "content_base64": z.string(),
    "requested_at": z.string().optional(),
}).passthrough();
export const AuditExportRequestSchema = z.object({
    "format": z.enum(["CSV"]).optional(),
    "from": z.string().optional(),
    "to": z.string().optional(),
    "tenant_id": UlidSchema.nullable().optional(),
    "event_code": z.string().max(80).optional(),
}).passthrough();
export const AuditIntegritySchema = z.object({
    "verified_through": UlidSchema.optional(),
    "row_count": z.number().int().optional(),
    "intact": z.boolean().optional(),
    "first_broken_id": UlidSchema.nullable().optional(),
    "checked_at": z.string().optional(),
}).passthrough();
export const AuditLogEntrySchema = z.object({
    "id": UlidSchema.optional(),
    "occurred_at": z.string().optional(),
    "tenant_id": UlidSchema.nullable().optional(),
    "actor_user_id": UlidSchema.nullable().optional(),
    "actor_kind": z.enum(["TENANT", "PLATFORM", "CONSULTANT", "SYSTEM", "ANONYMOUS"]).optional(),
    "event_code": z.string().optional(),
    "subject_type": z.string().optional(),
    "subject_id": z.string().nullable().optional(),
    "module_code": z.string().nullable().optional(),
    "request_id": z.string().nullable().optional(),
    "payload": z.record(z.string(), z.unknown()).optional(),
    "row_hash": z.string().optional(),
}).passthrough();
/** Hanya state VERIFIED dengan verified_by terisi yang masuk angka dampak. Ditegakkan CHECK di basis data dan view v_verified_benefit. */
export const BenefitSchema = z.object({
    "id": UlidSchema.optional(),
    "item_code": z.string().optional(),
    "kind": z.enum(["COST_SAVING", "TIME_SAVING", "QUALITY", "SAFETY", "OTHER"]).optional(),
    "amount": z.number().nullable().optional(),
    "currency": z.string().optional(),
    "quantity": z.number().nullable().optional(),
    "unit": z.string().nullable().optional(),
    "period_month": z.string().optional(),
    "state": z.enum(["DRAFT", "CLAIMED", "VERIFIED", "REJECTED"]).optional(),
    "claimed_by": UserRefSchema.optional(),
    "verified_by": UserRefSchema.nullable().optional(),
    "verified_at": z.string().nullable().optional(),
}).passthrough();
/** Apa yang sebenarnya terikat pada sesi ini di sisi basis data. Berguna untuk diagnosa dan untuk menampilkan 'Anda sedang bekerja sebagai' pada konsol konsultan dan pada tablet bersama. */
export const BoundContextSchema = z.object({
    "session_id": UlidSchema.optional(),
    "user_id": UlidSchema.optional(),
    "tenant_id": UlidSchema.nullable().optional(),
    "tenant_slug": z.string().nullable().optional(),
    "actor_kind": z.enum(["TENANT", "PLATFORM", "CONSULTANT", "SYSTEM"]).optional(),
    "session_kind": z.enum(["PERSONAL", "SHARED", "SERVICE"]).optional(),
    "role_code": z.string().nullable().optional(),
    "capabilities": z.array(z.string()).optional(),
    "expires_at": z.string().optional(),
}).passthrough();
/** Satu klien di portofolio konsultan yang MEMANGGIL. Perhatikan apa yang TIDAK ada di sini: tidak ada satu pun angka dari dalam organisasi klien. Peta §5.8 menjanjikan "ringkasan kesehatan per klien" pada layar ini, dan menepatinya menuntut agregat LINTAS tenant — yaitu tepat hal yang RLS tolak selama tenant belum terpasang pada baris sesi. Konsultan karena itu membuka satu klien dulu (POST /v1/auth/context/tenant, yang tercatat di jejak audit), lalu membaca angkanya di dalam konteks itu. Sebuah ringkasan yang diperoleh dengan melewati RLS bukan ringkasan; ia kebocoran dengan label lain. */
export const ClientPortfolioEntrySchema = z.object({
    "tenant_id": UlidSchema.optional(),
    "tenant_slug": z.string().optional(),
    "tenant_name": z.string().optional(),
    "state": z.enum(["PROVISIONING", "ACTIVE", "SUSPENDED", "CLOSED"]).optional(),
    "plan_code": z.string().nullable().optional(),
    "granted_at": z.string().optional(),
}).passthrough();
/** Satu baris catatan persetujuan. Tabelnya APPEND-ONLY: penarikan adalah baris BARU dengan withdrawn_at terisi, dan baris pemberian tidak pernah disentuh. Karena itu daftar ini adalah RIWAYAT, bukan keadaan — keadaan sekarang ada di medan `state` pada jawaban daftarnya, diturunkan dari baris terakhir per tujuan. */
export const ConsentRecordSchema = z.object({
    "id": UlidSchema,
    "purpose": z.enum(["NOTICE_ACKNOWLEDGED", "AVATAR"]),
    "kind": z.enum(["CONSENT", "ACKNOWLEDGEMENT"]).optional(),
    "basis": z.string(),
    "purposes": z.array(z.string()).optional(),
    "notice_version": z.number().int().nullable().optional(),
    "notice_locale": z.string().nullable().optional(),
    "granted_at": z.string().nullable().optional(),
    "withdrawn_at": z.string().nullable().optional(),
    "recorded_at": z.string(),
}).passthrough();
export const ConsentRequestSchema = z.object({
    "purpose": z.enum(["AVATAR"]),
    "granted": z.boolean(),
    "notice_id": UlidSchema.nullable().optional(),
}).passthrough();
/** Dasar pemrosesan satu operasi tulis, DINYATAKAN di module.manifest.json dan disalin apa adanya ke jawaban supaya kaki formulir dapat menyebutnya dalam dua bahasa. Klien merender kalimatnya dari kunci katalog privacy.basis.<legal_basis> dan privacy.purpose.<purpose>; server tidak pernah mengirim prosa (ADR T1-06). Dasar yang sama ikut dicatat ke payload setiap baris riwayat, sehingga pertanyaan 'atas dasar apa data ini diproses' dapat dijawab per BARIS. */
export const ProcessingBasisSchema = z.object({
    "legal_basis": z.enum(["CONSENT", "CONTRACT_PERFORMANCE", "LEGAL_OBLIGATION", "VITAL_INTEREST", "PUBLIC_TASK", "LEGITIMATE_INTEREST"]),
    "article": z.string(),
    "purposes": z.array(z.string()),
    "subject": z.enum(["SELF", "OTHERS", "BOTH", "NONE"]),
    "control": z.enum(["CHECKBOX", "BASIS_DISCLOSED", "NOTICE_ONLY"]),
    "consent_purpose": z.string().nullable().optional(),
}).passthrough();
export const ConsentResultSchema = z.object({
    "consent_id": UlidSchema,
    "purpose": z.string(),
    "effect": z.union([z.literal("AVATAR_DELETED"), z.literal(null)]).nullable().optional(),
    "processing_basis": ProcessingBasisSchema.nullable().optional(),
}).passthrough();
export const ConsentWithdrawRequestSchema = z.object({
    "purpose": z.enum(["AVATAR", "NOTICE_ACKNOWLEDGED"]),
}).passthrough();
/** Peta §5.8: konsultan hanya melihat tenant yang ditugaskan kepadanya. Sebelum telaah keamanan, aturan ini tidak punya tabel dan karenanya tidak dapat ditegakkan. */
export const ConsultantAssignmentSchema = z.object({
    "consultant_user_id": UlidSchema.optional(),
    "tenant_id": UlidSchema.optional(),
    "tenant_slug": z.string().optional(),
    "tenant_name": z.string().optional(),
    "consultant_name": z.string().nullable().optional(),
    "consultant_email": z.string().nullable().optional(),
    "granted_by": UlidSchema.optional(),
    "granted_by_name": z.string().nullable().optional(),
    "granted_at": z.string().optional(),
    "revoked_at": z.string().nullable().optional(),
}).passthrough();
/** Kirim tenant_id ATAU tenant_slug, tepat satu. `granted_by` dan `granted_at` TIDAK ada di badan: pemberi pengecualian isolasi tidak boleh dapat menuliskan nama orang lain sebagai pemberinya. */
export const ConsultantAssignmentCreateSchema = z.object({
    "consultant_user_id": UlidSchema,
    "tenant_id": UlidSchema.nullable().optional(),
    "tenant_slug": z.string().nullable().optional(),
}).passthrough();
export const DashboardSchema = z.object({
    "period": z.object({
        "from": z.string().optional(),
        "to": z.string().optional(),
    }).passthrough().optional(),
    "cards": z.array(z.object({
        "code": z.string().optional(),
        "module_code": z.string().optional(),
        "value": z.number().nullable().optional(),
        "unit": z.string().nullable().optional(),
        "delta_pct": z.number().nullable().optional(),
        "series": z.array(z.record(z.string(), z.unknown())).optional(),
    }).passthrough()).optional(),
    "areas": z.array(z.object({
        "location": LocationRefSchema.optional(),
        "open_count": z.number().int().optional(),
        "overdue_count": z.number().int().optional(),
        "completion_pct": z.number().nullable().optional(),
        "last_audit_pct": z.number().nullable().optional(),
    }).passthrough()).optional(),
    "verified_impact": z.object({
        "amount": z.number().optional(),
        "currency": z.string().optional(),
        "excluded_unverified_count": z.number().int().optional(),
    }).passthrough().optional(),
}).passthrough();
/** Satu perangkat yang masih memegang sesi. TIDAK memuat token: layar 'keluarkan dari semua perangkat' hanya perlu tahu perangkatnya, bukan rahasianya. */
export const DeviceSessionSchema = z.object({
    "id": UlidSchema,
    "device_label": z.string().nullable().optional(),
    "platform": z.string().nullable().optional(),
    "ip": z.string().nullable().optional(),
    "created_at": z.string(),
    "last_seen_at": z.string().nullable().optional(),
    "expires_at": z.string(),
    "is_current": z.boolean(),
}).passthrough();
/** Registry memvalidasi manifest.requires sebelum menyalakan; kekurangan prasyarat ditolak 409 MODULE_REQUIRES_MISSING dengan daftar modulnya di params. */
export const EntitlementUpdateSchema = z.object({
    "state": z.enum(["ENTITLED", "SUSPENDED", "DISABLED"]),
    "tier": z.string().optional(),
    "quota_value": z.number().int().nullable().optional(),
    "reason": z.string().optional(),
}).passthrough();
/** SATU SUMBER KEBENARAN entitlement (peta §7). Dibaca otorisasi API, navigasi konsol, dan aplikasi ponsel. Modul yang tidak ENTITLED tidak muncul di sini sama sekali kecuali sebagai DISABLED untuk keperluan layar 'modul di luar paket'. */
export const EntitlementsSchema = z.object({
    "tenant_id": UlidSchema.nullable(),
    "plan_code": z.string(),
    "etag": z.string(),
    "modules": z.array(z.object({
        "code": z.string().regex(new RegExp("^M[0-9]{2}$")),
        "state": z.enum(["ENTITLED", "SUSPENDED", "DISABLED"]),
        "tier": z.string().optional(),
        "metered_by": z.string().nullable().optional(),
        "quota_value": z.number().int().nullable().optional(),
        "usage_value": z.number().int().nullable().optional(),
    }).passthrough()),
}).passthrough();
/** Nilai sah diambil dari workflow_def yang dipatok pada butir — BUKAN enum tetap di API. */
export const ItemStateSchema = z.string();
export const FindingSchema = z.object({
    "id": UlidSchema.optional(),
    "code": z.string().nullable().optional(),
    "title": z.string().optional(),
    "description": z.string().max(300).nullable().optional(),
    "category_code": z.string().nullable().optional(),
    "location": LocationRefSchema.nullable().optional(),
    "asset": AssetRefSchema.nullable().optional(),
    "reported_by": UserRefSchema.optional(),
    "owner": UserRefSchema.nullable().optional(),
    "severity": z.enum(["LOW", "MEDIUM", "HIGH", "CRITICAL"]).nullable().optional(),
    "is_safety": z.boolean().optional(),
    "state": ItemStateSchema.optional(),
    "facets": z.array(z.enum(["OVERDUE", "ESCALATED"])).optional(),
    "escalation_level": z.number().int().min(0).max(4).optional(),
    "workflow_version": z.number().int().optional(),
    "due_at": z.string().nullable().optional(),
    "opened_at": z.string().optional(),
    "closed_at": z.string().nullable().optional(),
    "verified_at": z.string().nullable().optional(),
    "photo_count": z.number().int().optional(),
    "attachments": z.array(AttachmentSchema).optional(),
    "available_transitions": z.array(AvailableTransitionSchema).optional(),
    "processing_basis": ProcessingBasisSchema.nullable().optional(),
}).passthrough();
export const EscalationSchema = z.object({
    "id": UlidSchema.optional(),
    "item": FindingSchema.optional(),
    "level": z.number().int().min(1).max(4).optional(),
    "trigger_code": z.enum(["SLA_BREACH", "RECURRENCE_90D", "SAFETY_FLAG", "MANUAL"]).optional(),
    "target_role_code": z.string().optional(),
    "assignee": UserRefSchema.nullable().optional(),
    "opened_at": z.string().optional(),
    "due_at": z.string().optional(),
    "closed_at": z.string().nullable().optional(),
}).passthrough();
/** Mengubah aturan eskalasi wajib lewat dialog konfirmasi di klien dan menulis audit_log ENTITLEMENT-kelas di server (peta §5.3). */
export const EscalationRulesSchema = z.object({
    "levels": z.array(z.object({
        "level": z.number().int().min(1).max(4),
        "target_role_code": z.string(),
        "sla_hours": z.number().int().min(1),
        "triggers": z.array(z.enum(["SLA_BREACH", "RECURRENCE_90D", "SAFETY_FLAG"])).optional(),
        "state": z.enum(["ACTIVE", "PAUSED"]).optional(),
    }).passthrough()).max(4).min(4),
}).passthrough();
/** Memindahkan file_object dari PENDING ke COMMITTED dan mengonsumsi nonce URL unggahnya (sekali pakai). width_px/height_px yang dinyatakan klien DIABAIKAN sebagai kontrol — keduanya hanya dicatat di jejak audit sebagai declared_*, dan dimensi sesungguhnya dibaca dari header objek oleh pekerja verifikasi. Lampiran hanya boleh menunjuk berkas COMMITTED yang sudah TERVERIFIKASI; trigger basis data yang menegakkannya. */
export const FileCommitRequestSchema = z.object({
    "checksum_sha256": z.string().nullable().optional(),
    "byte_size": z.number().int().nullable().optional(),
    "parts": z.array(z.object({
        "part_number": z.number().int().optional(),
        "etag": z.string().optional(),
    }).passthrough()).nullable().optional(),
}).passthrough();
/** Objek yang BELUM diverifikasi dijawab 409 PHOTO_METADATA_PENDING; objek yang DIKARANTINA dijawab 404, sama dengan berkas yang tidak ada. */
export const FileDownloadUrlSchema = z.object({
    "download_url": z.string().optional(),
    "expires_at": z.string().optional(),
    "mime_type": z.string().optional(),
    "byte_size": z.number().int().nullable().optional(),
}).passthrough();
export const FindingCreateSchema = z.object({
    "id": UlidSchema,
    "title": z.string().max(200),
    "description": z.string().max(300).nullable().optional(),
    "category_code": z.string(),
    "location_id": UlidSchema,
    "asset_id": UlidSchema.nullable().optional(),
    "severity": z.enum(["LOW", "MEDIUM", "HIGH", "CRITICAL"]).nullable().optional(),
    "attachments": z.array(z.object({
        "file_id": UlidSchema,
        "role": z.enum(["BEFORE", "AFTER", "EVIDENCE"]),
    }).passthrough()).optional(),
    "occurred_at": z.string().optional(),
}).passthrough();
/** TIDAK ada role_code, dan ketiadaannya adalah pagarnya: rute ini selalu menerbitkan MGR. Sebuah medan peran akan menjadikan rute platform ini jalan menerbitkan peran apa pun di organisasi mana pun (temuan D-1). Ia menolak 409 ketika organisasinya sudah punya manajer aktif; sesudah itu penambahan orang berjalan dari dalam organisasi lewat POST /v1/invitations. */
export const FirstManagerInvitationRequestSchema = z.object({
    "email": z.string().max(160),
    "full_name": z.string().min(2).max(160),
    "employee_no": z.string().max(40).nullable().optional(),
    "job_title": z.string().max(160).nullable().optional(),
    "locale": z.enum(["id-ID", "en-US"]).nullable().optional(),
}).passthrough();
export const FirstManagerInvitationResultSchema = z.object({
    "id": UlidSchema,
    "invitation_id": UlidSchema,
    "email": z.string(),
    "full_name": z.string().optional(),
    "role_code": z.enum(["MGR"]),
    "state": z.enum(["INVITED"]),
    "first_manager": z.boolean().optional(),
    "reinvited": z.boolean().optional(),
    "tenant": z.object({
        "id": UlidSchema.optional(),
        "slug": z.string().optional(),
        "name": z.string().optional(),
    }).passthrough().optional(),
    "delivery_state": z.enum(["QUEUED", "WRITTEN_TO_FILE", "NOT_CONFIGURED"]),
    "delivery_configured": z.boolean(),
    "transport": z.enum(["queue", "file", "none"]).optional(),
    "invitation_url": z.string().nullable().optional(),
    "processing_basis": ProcessingBasisSchema.nullable().optional(),
}).passthrough();
export const HomeSummarySchema = z.object({
    "greeting_name": z.string().optional(),
    "cards": z.array(z.object({
        "code": z.string().optional(),
        "module_code": z.string().optional(),
    }).passthrough()).optional(),
    "points": z.object({
        "final": z.number().int().optional(),
        "provisional": z.number().int().optional(),
        "rank": z.number().int().nullable().optional(),
        "target": z.number().int().optional(),
    }).passthrough().optional(),
    "my_tasks": z.array(FindingSchema).optional(),
    "area_health": z.array(z.record(z.string(), z.unknown())).optional(),
    "unread_notifications": z.number().int().optional(),
    "pending_sync_count": z.number().int().optional(),
}).passthrough();
export const IncidentSchema = z.object({
    "id": UlidSchema.optional(),
    "severity": z.enum(["SEV1", "SEV2", "SEV3"]).optional(),
    "title": z.string().optional(),
    "detail": z.string().nullable().optional(),
    "related_job_code": z.string().nullable().optional(),
    "state": z.enum(["OPEN", "CLOSED"]).optional(),
    "opened_at": z.string().optional(),
    "closed_at": z.string().nullable().optional(),
}).passthrough();
/** `opened_by` TIDAK ada di badan: pelakunya diambil dari konteks sesi, seperti pada app.audit_write(). Sebuah catatan yang penulisnya dapat dipilih penulis itu sendiri bukan catatan. */
export const IncidentCreateSchema = z.object({
    "severity": z.enum(["SEV1", "SEV2", "SEV3"]),
    "title": z.string().min(3).max(200),
    "detail": z.string().max(4000).optional(),
    "related_job_code": z.string().max(80).optional(),
}).passthrough();
/** Aturan nomor 2 adendum A-01: tidak ada endpoint yang menerima kolom keadaan sebagai medan biasa. `action` menyatakan MAKSUD; pintu transisinya yang memutuskan apakah maksud itu sah dari keadaan sekarang. */
export const IncidentPatchSchema = z.object({
    "action": z.enum(["CLOSE", "REOPEN", "AMEND"]),
    "severity": z.enum(["SEV1", "SEV2", "SEV3"]).optional(),
    "detail": z.string().max(4000).optional(),
    "reason": z.string().max(500).optional(),
}).passthrough();
export const InvitationSchema = z.object({
    "id": UlidSchema.optional(),
    "email": z.string().optional(),
    "full_name": z.string().nullable().optional(),
    "role_code": z.string().optional(),
    "expires_at": z.string().optional(),
    "accepted_at": z.string().nullable().optional(),
    "revoked_at": z.string().nullable().optional(),
    "created_at": z.string().optional(),
}).passthrough();
/** Undangan yang TIDAK ADA, yang SUDAH TERPAKAI, yang DITARIK, dan yang KEDALUWARSA dijawab persis sama (404 INVITATION_INVALID); selisih di antara keempatnya adalah oracle keberadaan undangan. */
export const InvitationPreviewSchema = z.object({
    "organisation_name": z.string(),
    "organisation_slug": z.string().optional(),
    "email_hint": z.string().optional(),
    "role_code": z.string().optional(),
    "expires_at": z.string().optional(),
    "default_locale": z.enum(["id-ID", "en-US"]).optional(),
}).passthrough();
/** Token undangan TIDAK dikembalikan lewat HTTP: ia masuk ke antrean surel, di kolom yang hak SELECT-nya dicabut dari peran aplikasi. Seorang manajer yang dapat membaca token undangan orang lain dapat menetapkan kata sandinya, dan itu pengambilalihan akun dua langkah (kelas temuan T-04). Jawabannya menyatakan keadaan pengiriman apa adanya lewat delivery_state dan delivery_configured. */
export const InvitationRequestSchema = z.object({
    "email": z.string().max(160),
    "full_name": z.string().min(2).max(160),
    "role_code": z.enum(["OPR", "LDR", "MGR", "AUD", "FAC"]),
    "employee_no": z.string().max(40).nullable().optional(),
    "locale": z.enum(["id-ID", "en-US"]).nullable().optional(),
}).passthrough();
/** Sumber kebenaran riwayat butir (ADR-4). Tidak dapat diubah atau dihapus — hak UPDATE/DELETE dicabut di basis data. */
export const ItemEventSchema = z.object({
    "id": UlidSchema.optional(),
    "seq": z.number().int().optional(),
    "event_code": z.string().optional(),
    "from_state": z.string().nullable().optional(),
    "to_state": z.string().nullable().optional(),
    "actor": UserRefSchema.nullable().optional(),
    "actor_kind": z.enum(["TENANT", "PLATFORM", "CONSULTANT", "SYSTEM", "ANONYMOUS"]).optional(),
    "reason_code": z.string().nullable().optional(),
    "note": z.string().nullable().optional(),
    "occurred_at": z.string().optional(),
    "recorded_at": z.string().optional(),
    "workflow_version": z.number().int().optional(),
}).passthrough();
export const JobRunSchema = z.object({
    "id": UlidSchema.optional(),
    "job_code": z.string().optional(),
    "module_code": z.string().optional(),
    "tenant_id": UlidSchema.nullable().optional(),
    "state": z.enum(["QUEUED", "RUNNING", "SUCCEEDED", "FAILED", "SKIPPED_NO_ENTITLEMENT"]).optional(),
    "attempts": z.number().int().optional(),
    "scheduled_at": z.string().optional(),
    "started_at": z.string().nullable().optional(),
    "finished_at": z.string().nullable().optional(),
    "duration_ms": z.number().int().nullable().optional(),
    "error_code": z.string().nullable().optional(),
    "error_detail": z.string().nullable().optional(),
    "retry_of": UlidSchema.nullable().optional(),
    "requested_by": UlidSchema.nullable().optional(),
}).passthrough();
/** Peringkat hanya dari poin FINAL. Poin provisional ditampilkan terpisah agar peringkat tidak dapat digoyang dengan mengirim banyak laporan. */
export const LeaderboardSchema = z.object({
    "scope_kind": z.enum(["TENANT", "ORG_UNIT", "LOCATION"]).optional(),
    "scope_id": UlidSchema.nullable().optional(),
    "period_month": z.string().optional(),
    "me": z.object({
        "rank": z.number().int().optional(),
        "final_points": z.number().int().optional(),
        "provisional_points": z.number().int().optional(),
        "target_points": z.number().int().optional(),
    }).passthrough().optional(),
    "rows": z.array(z.object({
        "rank": z.number().int().optional(),
        "user": UserRefSchema.optional(),
        "final_points": z.number().int().optional(),
    }).passthrough()).optional(),
}).passthrough();
export const LocationSchema = LocationRefSchema.and(z.object({
    "description": z.string().nullable().optional(),
    "state": z.enum(["ACTIVE", "INACTIVE"]).optional(),
    "qr_printed": z.boolean().optional(),
}).passthrough());
export const LocationRequestSchema = z.object({
    "code": z.string().max(40),
    "name": z.string().max(160),
    "org_unit_id": UlidSchema,
    "description": z.string().max(300).nullable().optional(),
}).passthrough();
export const MeSchema = z.object({
    "id": UlidSchema.optional(),
    "tenant_id": UlidSchema.nullable().optional(),
    "full_name": z.string().optional(),
    "email": z.string().nullable().optional(),
    "employee_no": z.string().nullable().optional(),
    "job_title": z.string().nullable().optional(),
    "role_code": z.enum(["OPR", "LDR", "MGR", "AUD", "FAC", "KON", "PLT"]).nullable().optional(),
    "locale": z.enum(["id-ID", "en-US"]).optional(),
    "scope": z.object({
        "scope_all": z.boolean().optional(),
        "org_unit_ids": z.array(UlidSchema).optional(),
        "location_ids": z.array(UlidSchema).optional(),
    }).passthrough().optional(),
    "capabilities": z.array(z.string()).optional(),
    "avatar_url": z.string().nullable().optional(),
    "branding": z.record(z.string(), z.unknown()).optional(),
}).passthrough();
export const MeetingItemSchema = z.object({
    "id": UlidSchema.optional(),
    "seq": z.number().int().optional(),
    "title": z.string().optional(),
    "item_code": z.string().nullable().optional(),
    "discussed_at": z.string().nullable().optional(),
    "outcome_code": z.union([z.literal("RESOLVED"), z.literal("CARRIED_OVER"), z.literal("ACTION_CREATED"), z.literal("NOTED"), z.literal(null)]).nullable().optional(),
    "action_item_code": z.string().nullable().optional(),
}).passthrough();
export const MeetingSchema = z.object({
    "id": UlidSchema.optional(),
    "kind": z.enum(["DAILY_HUDDLE", "WEEKLY_REVIEW"]).optional(),
    "org_unit_id": UlidSchema.optional(),
    "facilitator": UserRefSchema.optional(),
    "state": z.enum(["SCHEDULED", "RUNNING", "OVERRUN", "CLOSED"]).optional(),
    "timebox_seconds": z.number().int().optional(),
    "elapsed_seconds": z.number().int().nullable().optional(),
    "scheduled_at": z.string().optional(),
    "started_at": z.string().nullable().optional(),
    "closed_at": z.string().nullable().optional(),
    "minutes_url": z.string().nullable().optional(),
    "items": z.array(MeetingItemSchema).optional(),
    "attendance": z.array(z.object({
        "user": UserRefSchema.optional(),
        "present": z.boolean().optional(),
    }).passthrough()).optional(),
    "available_transitions": z.array(AvailableTransitionSchema).optional(),
}).passthrough();
/** Keadaan LENGKAP daftar hadir: orang yang tidak ada di dalamnya memang tidak hadir. PUT dan bukan POST karena itu. */
export const MeetingAttendancePutSchema = z.object({
    "attendees": z.array(z.object({
        "user_id": UlidSchema,
        "present": z.boolean(),
    }).passthrough()).max(200),
}).passthrough();
/** Satu rapat harian per unit per slot waktu; bentrokan dijawab 409 MEETING_SLOT_TAKEN oleh constraint, bukan oleh pemeriksaan yang dapat kalah balapan. */
export const MeetingCreateSchema = z.object({
    "meeting_id": UlidSchema.nullable().optional(),
    "org_unit_id": UlidSchema,
    "location_id": UlidSchema.nullable().optional(),
    "scheduled_at": z.string(),
    "timebox_seconds": z.number().int().min(60).max(7200).optional(),
}).passthrough();
export const MeetingItemCreateSchema = z.object({
    "title": z.string().min(2).max(200),
    "item_id": UlidSchema.nullable().optional(),
}).passthrough();
export const MeetingItemPatchSchema = z.object({
    "discussed": z.boolean().optional(),
    "outcome_code": z.union([z.literal("RESOLVED"), z.literal("CARRIED_OVER"), z.literal("ACTION_CREATED"), z.literal("NOTED"), z.literal(null)]).nullable().optional(),
    "note": z.string().max(500).nullable().optional(),
}).passthrough();
/** Disusun dari manifest.provides.nav DAN entitlement. Tidak ada butir navigasi yang ditulis tetap di kode antarmuka. */
export const NavTreeSchema = z.object({
    "surface": z.enum(["console", "platform", "mobile"]).optional(),
    "groups": z.array(z.object({
        "code": z.enum(["OPERATIONS", "IMPROVEMENT", "KNOWLEDGE", "INSIGHT", "MANAGE", "PLATFORM", "GOVERNANCE", "TAB"]).optional(),
        "items": z.array(z.object({
            "label": z.string().optional(),
            "route": z.string().optional(),
            "icon": z.string().optional(),
            "module_code": z.string().optional(),
        }).passthrough()).optional(),
    }).passthrough()).optional(),
}).passthrough();
export const NotificationSchema = z.object({
    "id": UlidSchema.optional(),
    "event_code": z.string().optional(),
    "params": z.record(z.string(), z.unknown()).optional(),
    "target_route": z.string().optional(),
    "severity": z.enum(["INFO", "SUCCESS", "WARNING", "DANGER"]).optional(),
    "created_at": z.string().optional(),
    "read_at": z.string().nullable().optional(),
}).passthrough();
/** Jam tenang 20.00-06.00 menahan PUSH. Event berawalan SAFETY_ tidak dapat ditahan dan tidak dapat di-OFF-kan. */
export const NotificationPreferencesSchema = z.object({
    "items": z.array(z.object({
        "event_code": z.string(),
        "channel": z.enum(["PUSH", "EMAIL", "INAPP"]),
        "cadence": z.enum(["IMMEDIATE", "DIGEST_DAILY", "DIGEST_WEEKLY", "OFF"]),
    }).passthrough()).optional(),
}).passthrough();
export const OrgUnitSchema = z.object({
    "id": UlidSchema.optional(),
    "parent_id": UlidSchema.nullable().optional(),
    "code": z.string().optional(),
    "name": z.string().optional(),
    "depth": z.number().int().optional(),
    "state": z.enum(["ACTIVE", "INACTIVE"]).optional(),
}).passthrough();
/** depth dan path DIHITUNG basis data dari baris induknya, di dalam pernyataan yang sama dengan penyisipannya: path adalah yang dibaca app.visible_location_ids() untuk memutuskan cakupan area seseorang, jadi ia tidak boleh datang dari klien. */
export const OrgUnitRequestSchema = z.object({
    "code": z.string().max(40),
    "name": z.string().max(160),
    "parent_id": UlidSchema.nullable().optional(),
}).passthrough();
export const PageInfoSchema = z.object({
    "has_more": z.boolean(),
    "next_cursor": z.string().nullable().optional(),
    "total_estimate": z.number().int().nullable().optional(),
}).passthrough();
/** Rahasia penggelaran dikirim di header X-Platform-Bootstrap-Token, BUKAN di badan permintaan: badan permintaan masuk ke log validasi permintaan ketika ia ditolak, dan sebuah rahasia yang pernah masuk log bukan rahasia lagi. Kata sandinya di-hash di aplikasi dengan kebijakan yang sama seperti setiap pengguna lain; basis data tidak pernah melihatnya. */
export const PlatformBootstrapRequestSchema = z.object({
    "email": z.string().max(160),
    "full_name": z.string().min(2).max(160),
    "password": z.string().min(14).max(200),
}).passthrough();
/** Dikembalikan SEKALI. Permintaan kedua dengan rahasia yang sama dijawab 409 PLATFORM_BOOTSTRAP_ALREADY_CLAIMED, karena yang membuat jalan ini sekali pakai adalah baris tabel klaim — bukan hitungan baris PLT, yang akan terbuka kembali setiap kali admin terakhir hilang. */
export const PlatformBootstrapResultSchema = z.object({
    "user_id": UlidSchema,
    "email": z.string(),
    "role_code": z.string(),
    "claimed_at": z.string().optional(),
}).passthrough();
/** is_last_active_admin dihitung server, bukan di layar: layar yang menghitungnya sendiri akan salah pada saat dua orang membuka daftar yang sama, dan tombol Cabut yang hidup pada admin terakhir adalah tombol yang menjanjikan sesuatu yang basis data akan menolak. has_password menyatakan apakah orangnya sudah menetapkan kata sandinya — bukan hash-nya, dan bukan kapan. */
export const PlatformMembershipSchema = z.object({
    "user_id": UlidSchema,
    "email": z.string(),
    "full_name": z.string().nullable().optional(),
    "job_title": z.string().nullable().optional(),
    "role_code": z.enum(["PLT", "KON"]),
    "state": z.enum(["ACTIVE", "SUSPENDED"]),
    "user_state": z.string().optional(),
    "has_password": z.boolean().optional(),
    "granted_at": z.string().optional(),
    "granted_by_name": z.string().nullable().optional(),
    "revoked_at": z.string().nullable().optional(),
    "is_last_active_admin": z.boolean().optional(),
}).passthrough();
/** ALASAN wajib, dan itu bukan formalitas: app.platform_membership_set() menolak alasan kosong, karena sebuah peran platform yang berpindah tanpa alasan adalah baris yang tidak dapat dijelaskan setahun kemudian. Peran terbatas pada PLT dan KON — daftar tertutup yang sama dengan CHECK platform_membership_role_is_pqm. TIDAK ada medan kata sandi: pegawainya menetapkan kata sandinya sendiri lewat alur lupa kata sandi, sehingga tidak ada admin yang pernah mengetahui kata sandi orang lain. */
export const PlatformMembershipRequestSchema = z.object({
    "email": z.string().max(160),
    "full_name": z.string().min(2).max(160),
    "role_code": z.enum(["PLT", "KON"]),
    "job_title": z.string().max(160).nullable().optional(),
    "locale": z.enum(["id-ID", "en-US"]).nullable().optional(),
    "reason": z.string().min(3).max(400),
}).passthrough();
export const PointLedgerEntrySchema = z.object({
    "id": UlidSchema.optional(),
    "user": UserRefSchema.optional(),
    "rule_code": z.string().optional(),
    "points": z.number().int().optional(),
    "state": z.enum(["PROVISIONAL", "FINAL", "REVERSED"]).optional(),
    "period_month": z.string().optional(),
    "source_kind": z.string().optional(),
    "source_code": z.string().nullable().optional(),
    "approval_state": z.enum(["NOT_REQUIRED", "PENDING", "APPROVED", "REJECTED"]).optional(),
    "awarded_at": z.string().optional(),
}).passthrough();
/** Alasan WAJIB: poin yang hilang dari papan peringkat seseorang tanpa penjelasan adalah cara paling cepat membuat papan itu berhenti dipercaya. */
export const PointReverseRequestSchema = z.object({
    "reason": z.string().min(3).max(500),
}).passthrough();
export const PointRuleSchema = z.object({
    "code": z.string().optional(),
    "trigger_event": z.string().optional(),
    "subject_role": z.enum(["ACTOR", "REPORTER", "OWNER", "VERIFIER"]).optional(),
    "points": z.number().int().optional(),
    "award_state": z.enum(["PROVISIONAL", "FINAL"]).optional(),
    "cap_period": z.enum(["NONE", "DAY", "WEEK", "MONTH"]).optional(),
    "cap_value": z.number().int().nullable().optional(),
    "requires_approval": z.boolean().optional(),
    "state": z.enum(["ACTIVE", "PAUSED", "PENDING_APPROVAL", "CANCELLED"]).optional(),
}).passthrough();
export const PointRulePatchSchema = z.object({
    "points": z.number().int().min(1).max(1000).optional(),
    "cap_period": z.enum(["NONE", "DAY", "WEEK", "MONTH"]).optional(),
    "cap_value": z.number().int().nullable().optional(),
    "requires_approval": z.boolean().optional(),
    "state": z.enum(["ACTIVE", "PAUSED", "CANCELLED"]).optional(),
}).passthrough();
/** Pemberitahuan kepada subjek data (UU 27/2022 Pasal 21), BERVERSI dan dua bahasa. PUBLIC dan tanpa sesi: orang berada di layar masuk sebelum punya sesi, dan sebuah pemberitahuan yang hanya dapat dibaca setelah masuk adalah pemberitahuan yang terlambat. Kedua bahasa dikembalikan sekaligus karena orang yang baru didaftarkan belum punya profil dan karena itu belum punya bahasa. */
export const PrivacyNoticeSchema = z.object({
    "locale": z.enum(["id-ID", "en-US"]),
    "notices": z.array(z.object({
        "id": UlidSchema,
        "locale": z.enum(["id-ID", "en-US"]),
        "version": z.number().int(),
        "title": z.string(),
        "body": z.string(),
        "controller_statement": z.string(),
        "controller_name": z.string().nullable().optional(),
        "contact": z.string().optional(),
        "effective_from": z.string().optional(),
        "tenant_published": z.boolean().optional(),
    }).passthrough()),
}).passthrough();
/** Bentuk galat tunggal untuk seluruh API. Server TIDAK PERNAH mengirim prosa yang ditujukan kepada pengguna: ia mengirim code + params dan klien merendernya. Itulah cara janji 'semua string ada dalam dua bahasa sejak hari pertama' bisa ditepati tanpa menduplikasi katalog pesan di server. */
export const ProblemSchema = z.object({
    "code": z.string(),
    "status": z.number().int(),
    "params": z.record(z.string(), z.unknown()).optional(),
    "detail_en": z.string().optional(),
    "request_id": z.string().optional(),
    "field_errors": z.array(z.object({
        "path": z.string(),
        "code": z.string(),
        "params": z.record(z.string(), z.unknown()).optional(),
    }).passthrough()).optional(),
}).passthrough();
export const ProcessingAgreementSchema = z.object({
    "id": UlidSchema,
    "locale": z.enum(["id-ID", "en-US"]),
    "version": z.number().int(),
    "title": z.string(),
    "body": z.string(),
    "processor_name": z.string(),
    "contact": z.string(),
    "effective_from": z.string().optional(),
}).passthrough();
/** Daftar pemrosesan yang dirujuk perjanjian Pasal 51. DIRAKIT dari `processing_basis` di manifest modul — sumber yang SAMA dengan gerbang CI dan dengan dasar yang dicap basis data ke setiap baris riwayat — sehingga ia tidak dapat menyimpang dari apa yang benar-benar diproses. PUBLIC: calon organisasi pelanggan harus dapat membacanya SEBELUM menandatangani. Yang dikirim adalah KODE; kalimatnya ada di katalog dua bahasa (privacy.category.*, privacy.purpose.*, privacy.basis.*). */
export const ProcessingRegisterSchema = z.object({
    "write_operation_count": z.number().int(),
    "modules": z.array(z.object({
        "code": z.string(),
        "name": z.string(),
        "write_operations": z.number().int(),
        "categories": z.array(z.string()),
        "purposes": z.array(z.string()),
    }).passthrough()),
    "categories": z.array(z.object({
        "code": z.string(),
        "purposes": z.array(z.string()),
        "legal_bases": z.array(z.string()),
        "modules": z.array(z.string()),
    }).passthrough()),
    "purposes": z.array(z.object({
        "code": z.string(),
        "categories": z.array(z.string()),
        "modules": z.array(z.string()),
    }).passthrough()),
    "legal_bases": z.array(z.object({
        "code": z.string(),
        "article": z.string(),
        "write_operations": z.number().int(),
    }).passthrough()),
    "declared_but_unused": z.array(z.string()).optional(),
    "processing_locations": z.array(z.object({
        "aspect": z.enum(["DATABASE", "OBJECT_STORE", "CACHE", "PUSH_NOTIFICATION", "BACKUP"]).optional(),
        "region": z.string().optional(),
        "country": z.string().optional(),
        "declared_at": z.string().optional(),
    }).passthrough()).optional(),
    "subprocessors": z.array(z.object({
        "name": z.string().optional(),
        "role": z.string().optional(),
        "country": z.string().optional(),
        "added_at": z.string().optional(),
        "notified_at": z.string().nullable().optional(),
    }).passthrough()).optional(),
    "locations_declared": z.boolean().optional(),
    "subprocessors_declared": z.boolean().optional(),
}).passthrough();
/** Idempoten: kategori dan alur kerja yang sudah ada tidak digandakan. */
export const ProvisioningResultSchema = z.object({
    "tenant_id": UlidSchema,
    "item_categories": z.number().int(),
    "workflows": z.array(z.string()),
    "processing_basis": ProcessingBasisSchema.nullable().optional(),
}).passthrough();
export const QrResolveRequestSchema = z.object({
    "token": z.string(),
}).passthrough();
/** Kode milik tenant lain DITOLAK dengan 409 CODE_BELONGS_TO_ANOTHER_ORGANISATION — dibedakan dari 404 QR_TOKEN_UNKNOWN, karena pengguna lapangan perlu tahu bedanya. Pembedaan ini hanya membuka fakta kepemilikan, tidak membuka data apa pun. */
export const QrResolveResultSchema = z.object({
    "target_kind": z.enum(["LOCATION", "ASSET", "BOARD"]).optional(),
    "location": LocationRefSchema.nullable().optional(),
    "asset": AssetRefSchema.nullable().optional(),
}).passthrough();
export const QrTagSchema = z.object({
    "id": UlidSchema.optional(),
    "target_kind": z.enum(["LOCATION", "ASSET", "BOARD"]).optional(),
    "target_id": UlidSchema.optional(),
    "token": z.string().optional(),
    "printed_at": z.string().nullable().optional(),
    "print_payload_url": z.string().nullable().optional(),
}).passthrough();
export const ReportRunSchema = z.object({
    "id": UlidSchema.optional(),
    "report_kind": z.string().optional(),
    "format": z.enum(["CSV", "XLSX", "PDF"]).optional(),
    "state": z.enum(["QUEUED", "RUNNING", "READY", "FAILED", "EXPIRED"]).optional(),
    "download_url": z.string().nullable().optional(),
    "row_count": z.number().int().nullable().optional(),
    "error_code": z.string().nullable().optional(),
    "requested_at": z.string().optional(),
    "expires_at": z.string().nullable().optional(),
}).passthrough();
export const ReportRunRequestSchema = z.object({
    "report_kind": z.string(),
    "format": z.enum(["CSV", "XLSX", "PDF"]),
    "params": z.record(z.string(), z.unknown()).optional(),
}).passthrough();
export const SavedReportSchema = z.object({
    "id": UlidSchema.optional(),
    "code": z.string().optional(),
    "name": z.string().optional(),
    "report_kind": z.string().optional(),
    "scope": z.record(z.string(), z.unknown()).optional(),
    "schedule_cron": z.string().nullable().optional(),
    "format": z.enum(["CSV", "XLSX", "PDF"]).optional(),
    "recipients": z.array(z.record(z.string(), z.unknown())).optional(),
    "state": z.enum(["ACTIVE", "PAUSED"]).optional(),
    "last_run_at": z.string().nullable().optional(),
}).passthrough();
export const SavedReportPatchSchema = z.object({
    "name": z.string().min(3).max(160).optional(),
    "schedule_cron": z.string().nullable().optional(),
    "format": z.enum(["CSV", "XLSX", "PDF"]).optional(),
    "recipients": z.array(z.record(z.string(), z.unknown())).optional(),
    "state": z.enum(["ACTIVE", "PAUSED"]).optional(),
}).passthrough();
/** Hasil tunduk RLS DAN entitlement: entitas dari modul yang tidak dibeli tidak muncul. Kueri < 3 karakter ditolak 422 QUERY_TOO_SHORT. */
export const SearchResultsSchema = z.object({
    "query": z.string().optional(),
    "facets": z.array(z.object({
        "entity_type": z.string().optional(),
        "count": z.number().int().optional(),
    }).passthrough()).optional(),
    "results": z.array(z.object({
        "entity_type": z.string().optional(),
        "entity_id": UlidSchema.optional(),
        "ref_code": z.string().nullable().optional(),
        "title": z.string().optional(),
        "snippet": z.string().optional(),
        "route": z.string().optional(),
        "module_code": z.string().optional(),
    }).passthrough()).optional(),
    "page_info": PageInfoSchema.optional(),
}).passthrough();
export const SessionSchema = z.object({
    "access_token": z.string(),
    "refresh_token": z.string().nullable().optional(),
    "expires_at": z.string(),
    "user": MeSchema,
    "entitlements": EntitlementsSchema.optional(),
    "home_route": z.string().optional(),
}).passthrough();
export const SignInRequestSchema = z.object({
    "tenant_slug": z.string().nullable().optional(),
    "identity": z.string(),
    "password": z.string(),
    "device": DeviceInfoSchema.optional(),
}).passthrough();
/** Kalimat sorotan ditulis MANUSIA, jadi ia disimpan dalam dua kolom dan KEDUANYA wajib — bukan diterjemahkan katalog. */
export const SpotlightCreateSchema = z.object({
    "subject_kind": z.enum(["ITEM", "USER", "LOCATION"]),
    "subject_id": UlidSchema,
    "headline_id": z.string().min(3).max(200),
    "headline_en": z.string().min(3).max(200),
    "note": z.string().max(500).nullable().optional(),
    "period_month": z.string().optional(),
}).passthrough();
export const SpotlightEntrySchema = z.object({
    "id": UlidSchema.optional(),
    "period_month": z.string().optional(),
    "subject_kind": z.enum(["ITEM", "USER", "LOCATION"]).optional(),
    "subject_id": UlidSchema.optional(),
    "headline": z.string().optional(),
    "note": z.string().nullable().optional(),
}).passthrough();
/** Paket yang diunduh saat masuk agar Report a Finding, My Tasks, Close Out a Finding, Offline Audit, dan Audit Score Summary bekerja penuh tanpa sinyal (peta §7). */
export const SyncBootstrapSchema = z.object({
    "server_time": z.string().optional(),
    "cursor": z.string().optional(),
    "entitlements": EntitlementsSchema.optional(),
    "locations": z.array(LocationSchema).optional(),
    "assets": z.array(AssetSchema).optional(),
    "item_categories": z.array(z.record(z.string(), z.unknown())).optional(),
    "instruments": z.array(InstrumentSchema).optional(),
    "workflows": z.array(z.record(z.string(), z.unknown())).optional(),
    "users": z.array(UserRefSchema).optional(),
}).passthrough();
export const SyncChangesSchema = z.object({
    "cursor": z.string().optional(),
    "has_more": z.boolean().optional(),
    "changes": z.array(z.object({
        "entity_type": z.string().optional(),
        "entity_id": UlidSchema.optional(),
        "op": z.enum(["UPSERT", "TOMBSTONE"]).optional(),
        "body": z.record(z.string(), z.unknown()).optional(),
    }).passthrough()).optional(),
}).passthrough();
/** Mutasi adalah NIAT, bukan hasil (peta §7). Batch diproses satu per satu dalam urutan client_seq; satu mutasi yang ditolak TIDAK menggagalkan yang lain. Mengirim ulang mutation_id yang sama mengembalikan hasil yang sama tanpa efek samping kedua — idempotensi datang dari ULID klien (ADR-5), bukan dari header Idempotency-Key tambahan. */
export const SyncMutationBatchSchema = z.object({
    "device_id": z.string(),
    "mutations": z.array(z.object({
        "mutation_id": UlidSchema,
        "kind": z.enum(["FINDING_CREATE", "FINDING_TRANSITION", "FINDING_COMMENT", "ASSESSMENT_CREATE", "ASSESSMENT_ANSWERS", "ASSESSMENT_TRANSITION", "ACTION_TRANSITION", "NOTIFICATION_READ"]),
        "client_seq": z.number().int().nullable().optional(),
        "client_created_at": z.string(),
        "payload": z.record(z.string(), z.unknown()),
    }).passthrough()).max(200).min(1),
}).passthrough();
/** Tanggapan 200 meski ada mutasi yang ditolak: status HTTP menggambarkan batch, bukan tiap mutasi. */
export const SyncMutationResultSchema = z.object({
    "results": z.array(z.object({
        "mutation_id": UlidSchema.optional(),
        "state": z.enum(["APPLIED", "ACCEPTED", "REJECTED", "DUPLICATE"]).optional(),
        "server_ref": z.record(z.string(), z.unknown()).nullable().optional(),
        "rejection": ProblemSchema.nullable().optional(),
    }).passthrough()).optional(),
}).passthrough();
export const SystemHealthSchema = z.object({
    "outbox": z.object({
        "pending": z.number().int().optional(),
        "failed": z.number().int().optional(),
        "dead": z.number().int().optional(),
        "oldest_pending_age_seconds": z.number().int().optional(),
    }).passthrough().optional(),
    "jobs": z.array(z.object({
        "job_code": z.string().optional(),
        "last_state": z.string().optional(),
        "last_finished_at": z.string().nullable().optional(),
        "failures_24h": z.number().int().optional(),
    }).passthrough()).optional(),
    "latency": z.object({
        "p50_ms": z.number().optional(),
        "p95_ms": z.number().optional(),
        "p99_ms": z.number().optional(),
    }).passthrough().optional(),
    "incidents": z.array(IncidentSchema).optional(),
    "audit_chain": AuditIntegritySchema.optional(),
}).passthrough();
/** DIHITUNG lewat sesi layanan yang dipatok ke satu organisasi, dan hanya ANGKA yang menyeberang — nol nama, nol surel. Untuk tenant yang belum ACTIVE ia tidak dibaca sama sekali: app.session_bind menolak sesi layanan ke tenant non-aktif sejak migrasi 0034. */
export const TenantOnboardingSchema = z.object({
    "readable": z.boolean(),
    "agreement_accepted": z.boolean().optional(),
    "item_categories": z.number().int().optional(),
    "workflows": z.number().int().optional(),
    "active_managers": z.number().int().optional(),
    "open_invitations": z.number().int().optional(),
    "remaining_steps": z.array(z.enum(["AGREEMENT_PENDING", "PROVISIONING_PENDING", "NO_MANAGER_INVITED", "MANAGER_INVITED_NOT_IN"])).optional(),
    "operable": z.boolean().optional(),
}).passthrough();
export const TenantSchema = z.object({
    "id": UlidSchema.optional(),
    "slug": z.string().optional(),
    "name": z.string().optional(),
    "legal_name": z.string().nullable().optional(),
    "program_id": UlidSchema.nullable().optional(),
    "plan_code": z.string().optional(),
    "state": z.enum(["PROVISIONING", "ACTIVE", "SUSPENDED", "CLOSED"]).optional(),
    "branding": z.record(z.string(), z.unknown()).optional(),
    "default_locale": z.enum(["id-ID", "en-US"]).optional(),
    "timezone": z.string().optional(),
    "created_at": z.string().optional(),
    "onboarding": TenantOnboardingSchema.nullable().optional(),
}).passthrough();
/** `accepted` null berarti organisasi ini BELUM DIBUKA, dan itu keadaan — bukan galat. Jawabannya tetap 200 supaya layarnya dapat menyatakan apa yang kurang dan siapa yang harus bertindak, alih-alih menampilkan halaman galat kepada orang yang tidak dapat melakukan apa pun tentangnya. */
export const TenantAgreementSchema = z.object({
    "agreement": ProcessingAgreementSchema.nullable(),
    "accepted": AgreementAcceptanceSchema.nullable(),
}).passthrough();
/** Butir P8. Satu baris jejak audit MILIK ORGANISASI INI. Batas barisnya ditegakkan kebijakan RESTRICTIVE `audit_tenant_scope`, bukan kueri handler: seorang manajer memperoleh jejak organisasinya, dan tidak memperoleh baris platform maupun baris organisasi lain. Hak Pengendali memeriksa pemrosesan Prosesornya (Pasal 51) tidak dapat dipenuhi bila satu-satunya yang dapat membaca jejak adalah Prosesornya. */
export const TenantAuditEntrySchema = z.object({
    "id": UlidSchema,
    "occurred_at": z.string(),
    "actor_kind": z.string(),
    "actor_name": z.string().nullable().optional(),
    "event_code": z.string(),
    "subject_type": z.string(),
    "subject_id": z.string().nullable().optional(),
    "module_code": z.string().nullable().optional(),
    "ip": z.string().nullable().optional(),
    "processing_basis": z.string().nullable().optional(),
}).passthrough();
/** Perpindahan konteks tenant untuk pengguna internal PQM. Tenant TIDAK PERNAH dipilih lewat header: parameter X-Tenant-Slug sengaja DIHAPUS dari kontrak ini setelah telaah keamanan K-04. Server memeriksa consultant_assignment, menulis CONSULTANT_CONTEXT_SWITCHED ke audit_log, lalu mengubah baris sesi. Konsultan yang tidak ditugaskan menerima 403 CONSULTANT_NOT_ASSIGNED. */
export const TenantContextSwitchSchema = z.object({
    "tenant_slug": z.string(),
}).passthrough();
/** Uji terima: dari permintaan ini sampai tenant siap dipakai < 30 menit, tanpa penggelaran. */
export const TenantCreateSchema = z.object({
    "id": UlidSchema,
    "slug": z.string(),
    "name": z.string(),
    "legal_name": z.string().nullable().optional(),
    "plan_code": z.string(),
    "program_id": UlidSchema.nullable().optional(),
    "default_locale": z.enum(["id-ID", "en-US"]).optional(),
    "timezone": z.string().optional(),
    "first_admin": z.object({
        "email": z.string().optional(),
        "full_name": z.string().optional(),
    }).passthrough().optional(),
}).passthrough();
/** Identitas organisasi seperti yang dibaca layar. Nama kunci logo_mark_url dipertahankan dari comment on table tenant (migrasi 0002) justru di tempat ia dibaca; yang menyimpang adalah apa yang tersimpan. */
export const TenantIdentitySchema = z.object({
    "tenant_id": UlidSchema,
    "slug": z.string().optional(),
    "name": z.string().optional(),
    "legal_name": z.string().nullable().optional(),
    "plan_code": z.string().optional(),
    "state": z.string().optional(),
    "default_locale": z.enum(["id-ID", "en-US"]).optional(),
    "timezone": z.string().optional(),
    "product_name": z.string().optional(),
    "accent_hex": z.string().nullable().optional(),
    "logo_mark_url": z.string().nullable().optional(),
    "logo_state": z.enum(["NONE", "VERIFYING", "READY"]),
    "logo_byte_size": z.number().int().nullable().optional(),
    "logo_width_px": z.number().int().nullable().optional(),
    "logo_height_px": z.number().int().nullable().optional(),
}).passthrough();
/** LINGKUP MGR (ADR-I1). legal_name, slug, dan plan_code TIDAK ADA di sini, dan ketiadaannya struktural: nama badan hukum muncul pada perjanjian pemrosesan data yang sudah ditandatangani, jadi ia tidak dapat diubah pihak yang menandatanganinya. */
export const TenantIdentityUpdateSchema = z.object({
    "name": z.string().min(2).max(120).optional(),
    "default_locale": z.enum(["id-ID", "en-US"]).optional(),
    "timezone": z.enum(["Asia/Jakarta", "Asia/Makassar", "Asia/Jayapura", "UTC"]).optional(),
    "accent_hex": z.string().regex(new RegExp("^#[0-9a-fA-F]{6}$")).optional(),
    "logo_mark_file_id": UlidSchema.optional(),
    "hapus_logo": z.boolean().optional(),
}).passthrough();
/** ADR-I1: rute ini pemilik TUNGGAL kolom plan_code. PATCH /v1/platform/tenants/{tenantId} sengaja tidak mengenal kunci ini sama sekali. Mengubah paket TIDAK menyalakan entitlement: itu tetap satu tindakan sadar per modul, lewat PUT .../entitlements/{moduleCode} yang menghitung butir berjalan sebelum mematikan apa pun. */
export const TenantPlanPutSchema = z.object({
    "plan_code": z.string().max(40),
    "reason": z.string().max(500).optional(),
}).passthrough();
export const TenantReadinessSchema = z.object({
    "tenant_id": UlidSchema.optional(),
    "counts": z.record(z.string(), z.number().int()).optional(),
    "remaining_steps": z.array(z.enum(["ORG_UNIT_MISSING", "LOCATION_MISSING", "ITEM_CATEGORY_MISSING", "WORKFLOW_MISSING", "NO_ONE_INVITED"])).optional(),
    "operable": z.boolean().optional(),
    "delivery_state": z.enum(["QUEUED", "WRITTEN_TO_FILE", "NOT_CONFIGURED"]).optional(),
    "delivery_configured": z.boolean().optional(),
    "transport": z.enum(["queue", "file", "none"]).optional(),
}).passthrough();
/** Alasan komersial atau kepatuhan, opsional dan DICATAT ketika ada. Ia tidak diwajibkan: memaksa orang mengetik sesuatu hanya menghasilkan titik. */
export const TenantSuspendRequestSchema = z.object({
    "reason": z.string().max(500).optional(),
}).passthrough();
/** LINGKUP PLT (ADR-I1). Tiga medan yang mengikat kontrak, dan tidak satu pun medan harian. Warna aksen, zona waktu, bahasa bawaan, dan logo BUKAN kunci yang dikenal di sini: additionalProperties false menolak permintaannya alih-alih mengabaikannya diam-diam. `plan_code` punya rutenya sendiri (PUT .../plan) supaya satu kolom tidak punya dua jalan dan dua tempat untuk lupa menulis jejaknya. */
export const TenantUpdateSchema = z.object({
    "name": z.string().max(200).optional(),
    "legal_name": z.string().max(200).optional(),
    "slug": z.string().min(3).max(63).regex(new RegExp("^[a-z0-9]+(-[a-z0-9]+)*$")).optional(),
}).passthrough();
export const TransitionRequestSchema = z.object({
    "mutation_id": UlidSchema,
    "transition": z.string(),
    "reason_code": z.string().nullable().optional(),
    "note": z.string().nullable().optional(),
    "expected_state": z.string().nullable().optional(),
    "expected_workflow_version": z.number().int().nullable().optional(),
    "input": z.record(z.string(), z.unknown()).optional(),
    "occurred_at": z.string().nullable().optional(),
}).passthrough();
export const TransitionResultSchema = z.object({
    "item": FindingSchema.optional(),
    "event": ItemEventSchema.optional(),
    "side_effects": z.array(z.object({
        "kind": z.string().optional(),
        "detail": z.record(z.string(), z.unknown()).optional(),
    }).passthrough()).optional(),
}).passthrough();
/** SATU niat unggah per permintaan (objek datar, bukan batch). Foto lapangan dibatasi 300 KB dan sisi terpanjang 1600 px; byte_size di atas batas ditolak 422 PHOTO_TOO_LARGE tanpa pernah mengunggah apa pun, dan batas yang sama DIIKAT tanda tangan URL-nya. */
export const UploadIntentRequestSchema = z.object({
    "file_id": UlidSchema.optional(),
    "mime_type": z.enum(["image/jpeg", "image/png", "image/webp", "application/pdf", "text/csv", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"]),
    "byte_size": z.number().int().max(26214400).nullable().optional(),
    "filename": z.string().max(255).nullable().optional(),
    "purpose": z.enum(["ITEM_PHOTO", "ASSESSMENT_EVIDENCE", "BENEFIT_EVIDENCE", "AVATAR", "REPORT_EXPORT", "MEETING_MINUTES"]),
}).passthrough();
export const UploadIntentResultSchema = z.object({
    "file_id": UlidSchema,
    "bucket": z.string().optional(),
    "object_key": z.string().optional(),
    "upload_url": z.string(),
    "expires_at": z.string(),
    "resumable": z.boolean().optional(),
    "max_byte_size": z.number().int().optional(),
    "max_pixel_edge": z.number().int().nullable().optional(),
    "filename": z.string().nullable().optional(),
    "verification_required": z.boolean(),
}).passthrough();
export const UsageReportSchema = z.object({
    "tenant_id": UlidSchema.optional(),
    "period_month": z.string().optional(),
    "meters": z.array(z.object({
        "metered_by": z.string().optional(),
        "module_code": z.string().optional(),
        "value": z.number().int().nullable().optional(),
        "quota_value": z.number().int().nullable().optional(),
    }).passthrough()).optional(),
}).passthrough();
export const UserSchema = UserRefSchema.and(z.object({
    "email": z.string().nullable().optional(),
    "employee_no": z.string().nullable().optional(),
    "job_title": z.string().nullable().optional(),
    "role_code": z.string().nullable().optional(),
    "state": z.enum(["INVITED", "ACTIVE", "DISABLED", "LOCKED"]).optional(),
    "scope": z.record(z.string(), z.unknown()).optional(),
    "last_login_at": z.string().nullable().optional(),
}).passthrough());
/** Jawaban GET /v1/me/sessions, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetMeSessionsResponseSchema = z.object({
    "items": z.array(DeviceSessionSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/notifications, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetNotificationsResponseSchema = z.object({
    "items": z.array(NotificationSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/org-units, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetOrgUnitsResponseSchema = z.object({
    "items": z.array(OrgUnitSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/locations, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetLocationsResponseSchema = z.object({
    "items": z.array(LocationSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/locations/{locationId}/qr-tags, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetLocationsByLocationIdQrTagsResponseSchema = z.object({
    "items": z.array(QrTagSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/assets, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetAssetsResponseSchema = z.object({
    "items": z.array(AssetSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/users, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetUsersResponseSchema = z.object({
    "items": z.array(UserSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/users/{userId}/activity, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetUsersByUserIdActivityResponseSchema = z.object({
    "items": z.array(ItemEventSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/invitations, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetInvitationsResponseSchema = z.object({
    "items": z.array(InvitationSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/me/consents, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetMeConsentsResponseSchema = z.object({
    "items": z.array(ConsentRecordSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/findings, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetFindingsResponseSchema = z.object({
    "items": z.array(FindingSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/findings/{code}/events, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetFindingsByCodeEventsResponseSchema = z.object({
    "items": z.array(ItemEventSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/findings/{code}/comments, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetFindingsByCodeCommentsResponseSchema = z.object({
    "items": z.array(AcknowledgedSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/verification-queue, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetVerificationQueueResponseSchema = z.object({
    "items": z.array(FindingSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/item-categories, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetItemCategoriesResponseSchema = z.object({
    "items": z.array(AcknowledgedSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/me/tasks, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetMeTasksResponseSchema = z.object({
    "items": z.array(FindingSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/meetings, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetMeetingsResponseSchema = z.object({
    "items": z.array(MeetingSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/escalations, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetEscalationsResponseSchema = z.object({
    "items": z.array(EscalationSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/spotlight, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetSpotlightResponseSchema = z.object({
    "items": z.array(SpotlightEntrySchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/actions, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetActionsResponseSchema = z.object({
    "items": z.array(FindingSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/point-rules, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetPointRulesResponseSchema = z.object({
    "items": z.array(PointRuleSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/point-ledger, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetPointLedgerResponseSchema = z.object({
    "items": z.array(PointLedgerEntrySchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/dashboard/decision-queue, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetDashboardDecisionQueueResponseSchema = z.object({
    "items": z.array(FindingSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/report-catalog, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetReportCatalogResponseSchema = z.object({
    "items": z.array(SavedReportSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/saved-reports, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetSavedReportsResponseSchema = z.object({
    "items": z.array(SavedReportSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/benefits, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetBenefitsResponseSchema = z.object({
    "items": z.array(BenefitSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/me/reports, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetMeReportsResponseSchema = z.object({
    "items": z.array(FindingSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/platform/tenants, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetPlatformTenantsResponseSchema = z.object({
    "items": z.array(TenantSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/platform/tenants/{tenantId}/provisioning-log, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetPlatformTenantsByTenantIdProvisioningLogResponseSchema = z.object({
    "items": z.array(AuditLogEntrySchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/platform/modules, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetPlatformModulesResponseSchema = z.object({
    "items": z.array(AcknowledgedSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/platform/plans, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetPlatformPlansResponseSchema = z.object({
    "items": z.array(AcknowledgedSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/platform/audit, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetPlatformAuditResponseSchema = z.object({
    "items": z.array(AuditLogEntrySchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/platform/jobs, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetPlatformJobsResponseSchema = z.object({
    "items": z.array(JobRunSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/platform/incidents, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetPlatformIncidentsResponseSchema = z.object({
    "items": z.array(IncidentSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/me/assignments, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetMeAssignmentsResponseSchema = z.object({
    "items": z.array(ClientPortfolioEntrySchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/platform/consultant-assignments, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetPlatformConsultantAssignmentsResponseSchema = z.object({
    "items": z.array(ConsultantAssignmentSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/platform/audit/anchors, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetPlatformAuditAnchorsResponseSchema = z.object({
    "items": z.array(AuditAnchorSchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/tenant/audit, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetTenantAuditResponseSchema = z.object({
    "items": z.array(TenantAuditEntrySchema),
    "page_info": PageInfoSchema,
}).passthrough();
/** Jawaban GET /v1/platform/memberships, dibangkitkan dari bentuk sebaris di openapi.yaml. */
export const GetPlatformMembershipsResponseSchema = z.object({
    "items": z.array(PlatformMembershipSchema),
    "page_info": PageInfoSchema,
}).passthrough();
//# sourceMappingURL=schemas.generated.js.map